import React, { useEffect } from 'react';
import { useApi, configApiRef, useAnalytics } from '@backstage/core-plugin-api';
import { CodeSnippet, InfoCard, Link } from '@backstage/core-components';
import { useTechDocsReaderPage } from '@backstage/plugin-techdocs-react';
import { useLocation } from 'react-router-dom';
import { Typography, Box } from '@material-ui/core';

type Props = {
  errorMessage?: string;
};

export const TechDocsNotFound = ({ errorMessage }: Props) => {
  const techdocsBuilder =
    useApi(configApiRef).getOptionalString('techdocs.builder');
  const analyticsApi = useAnalytics();
  const { entityRef } = useTechDocsReaderPage();
  const location = useLocation();

  useEffect(() => {
    const { pathname, search, hash } = location;
    analyticsApi.captureEvent('not-found', `${pathname}${search}${hash}`, {
      attributes: entityRef,
    });
  }, [analyticsApi, entityRef, location]);

  if (techdocsBuilder !== 'local') {
    console.error(
        errorMessage +
        "Note that techdocs.builder is not set to 'local' in your config, which means this Backstage app will not " +
        "generate docs if they are not found. Make sure the project's docs are generated and published by some external " +
        "process (e.g. CI/CD pipeline). Or change techdocs.builder to 'local' to generate docs from this Backstage " +
        'instance.');
  }

  return (
    <Box justifyContent="center" alignItems="center" minHeight="60vh">
      <InfoCard title="Documentation Not Set Up">
        <Typography variant="body1" paragraph>
          The documentation for this entity has not been published yet.
        </Typography>
        <Typography variant="body1" paragraph>
          To set up documentation for your repository, run the following command in your terminal:
        </Typography>
        <CodeSnippet text="bart docs setup" language="bash" showCopyCodeButton />
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            This will configure the necessary documentation workflows in your repository.
            See the <Link
          to={`${window.location.origin}/catalog/default/component/bart/docs/commands/docs/setup`}>documentation</Link> for more details.
          </Typography>
        </Box>
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            <strong>Note:</strong> If your documentation is already set up but you're seeing this page, 
            there may be a temporary issue fetching the docs. Please try refreshing the page.
          </Typography>
        </Box>
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            For support, reach out to the DevX team on <Link
          to={"https://app.slack.com/client/T029XC9S7/C01148W2F8B"}>#dev-experience</Link> channel.
          </Typography>
        </Box>
      </InfoCard>
    </Box>
  );
};
